import {
  ApplicationException,
  isNullOrUndefined,
  ReservationStatusOrder,
  RideRequestTypeConstants,
  ServiceLevelConstants,
  ServiceLevelType,
  VehicleTypeConstants
} from '@uzurv/core';
import { Organization, OrganizationRiderServiceLevel, OrganizationServiceLevel, RideRequestDetail, VehicleType } from '../models';
import { RideRequest } from '../models/ride-request';

export class RideUtil {
  static compareReservationStatus(firstStatusName: string, secondStatusName: string): number {
    const firstStatusOrder: ReservationStatusOrder = ReservationStatusOrder[firstStatusName];
    const secondStatusOrder: ReservationStatusOrder = ReservationStatusOrder[secondStatusName];

    if (firstStatusOrder < secondStatusOrder) {
      return -1;
    }

    if (firstStatusOrder > secondStatusOrder) {
      return 1;
    }

    return 0;
  }
  // TODO: Refactor this to use a 'hasPickupDate' (?) interface so we can have a single method here
  static sortRideRequestsByPickupDate(rideRequests: Array<RideRequest>, ascending = true): Array<RideRequest> {
    if (isNullOrUndefined(rideRequests)) {
      return [];
    }

    return rideRequests.sort((a, b) => {
      const aPickupDate = a.pickupDate;
      const bPickupDate = b.pickupDate;

      // Always sort ON Demand as last.
      if (isNullOrUndefined(aPickupDate)) {
        return 1;
      }
      if (isNullOrUndefined(bPickupDate)) {
        return -1;
      }

      if (ascending) {
        return aPickupDate.getTime() - bPickupDate.getTime();
      } else {
        return bPickupDate.getTime() - aPickupDate.getTime();
      }
    });
  }

  static isRideRequestDetailJourneyEligible(rideRequestDetail: RideRequestDetail) {
    if (isNullOrUndefined(rideRequestDetail)) {
      return false;
    }

    if (rideRequestDetail.rideRequestType.onDemand) {
      return false;
    }

    // TODO: Refactor as service level check once available
    return rideRequestDetail.vehicleType.code === VehicleTypeConstants.VEHICLE_TYPE_CODE_BASIC;
  }

  static updateRideRequestServiceLevel(
    rideRequest: RideRequest,
    serviceLevelType: ServiceLevelType,
    organizationServiceLevels: Array<OrganizationServiceLevel>
  ): RideRequest {
    if (serviceLevelType === ServiceLevelType.AMBULATORY) {
      const ad2dServiceLevel = organizationServiceLevels.find(
        osl => osl.serviceLevel.code === ServiceLevelConstants.AMBULATORY_DOOR_TO_DOOR_CODE
      );

      const ac2cServiceLevel = organizationServiceLevels.find(
        osl => osl.serviceLevel.code === ServiceLevelConstants.AMBULATORY_CURB_TO_CURB_CODE
      );

      if (rideRequest.rideBackupAccepted && !isNullOrUndefined(ac2cServiceLevel)) {
        rideRequest.serviceLevel = ac2cServiceLevel.serviceLevel;
      } else {
        if (isNullOrUndefined(ad2dServiceLevel)) {
          throw new ApplicationException('Missing required service level');
        }

        rideRequest.serviceLevel = ad2dServiceLevel.serviceLevel;
      }
    } else if (serviceLevelType === ServiceLevelType.NON_AMBULATORY) {
      const nad2dServiceLevel = organizationServiceLevels.find(
        osl => osl.serviceLevel.code === ServiceLevelConstants.NON_AMBULATORY_DOOR_TO_DOOR_CODE
      );

      if (isNullOrUndefined(nad2dServiceLevel)) {
        throw new ApplicationException('Missing required service level');
      }

      rideRequest.serviceLevel = nad2dServiceLevel.serviceLevel;
    }

    return rideRequest;
  }

  static getD2DServiceLevelIdForRideRequestInfo(
    vehicleType: VehicleType,
    organizationServiceLevels: Array<OrganizationServiceLevel>
  ): number {
    // Convert the vehicle type (Basic/WAV) to a proper service level.
    // Defaulting to door to door option for both ambulatory and non-ambulatory.
    if (vehicleType.code === VehicleTypeConstants.VEHICLE_TYPE_CODE_BASIC) {
      const ad2d: OrganizationServiceLevel = organizationServiceLevels.find(
        osl => osl.serviceLevel.code === ServiceLevelConstants.AMBULATORY_DOOR_TO_DOOR_CODE
      );

      return ad2d.serviceLevel.id;
    } else if (vehicleType.code === VehicleTypeConstants.VEHICLE_TYPE_CODE_WAV) {
      const nad2d: OrganizationServiceLevel = organizationServiceLevels.find(
        osl => osl.serviceLevel.code === ServiceLevelConstants.NON_AMBULATORY_DOOR_TO_DOOR_CODE
      );

      return nad2d.serviceLevel.id;
    }
  }

  static isRideBackupOptional(
    organization: Organization,
    organizationServiceLevels: Array<OrganizationServiceLevel>,
    organizationRiderServiceLevels: Array<OrganizationRiderServiceLevel>,
    newRideRequest: RideRequest
  ): boolean {
    // If the rider MUST pay, they CAN pay via fare collection, and CANNOT pay via card/account, don't ask for backup preference.
    if (organization.riderPaymentRequired && organization.riderFareCollectionEnabled && !organization.riderPaymentAccountEnabled) {
      return false;
    }

    const ac2cOrganizationServiceLevel: OrganizationServiceLevel = organizationServiceLevels.find(
      osl => osl.serviceLevel.code === ServiceLevelConstants.AMBULATORY_CURB_TO_CURB_CODE
    );

    // If the organization does not consider curb to curb as an optional service level,
    // then ride back up will default to true and they cannot change it
    if (!isNullOrUndefined(ac2cOrganizationServiceLevel) && !ac2cOrganizationServiceLevel.optional) {
      return false;
    }

    const ac2cRiderServiceLevel: OrganizationRiderServiceLevel = organizationRiderServiceLevels?.find(
      orsl => orsl.serviceLevel.code === ServiceLevelConstants.AMBULATORY_CURB_TO_CURB_CODE
    );

    // If the rider is always ok with curb to curb,
    // then ride back up will default to true and they cannot change it
    if (ac2cRiderServiceLevel?.enabled) {
      return false;
    }

    return newRideRequest?.rideBackupEligible;
  }

  static isRideNowEnabled(vehicleType: VehicleType, organizationServiceLevels: Array<OrganizationServiceLevel>): boolean {
    // Convert the vehicle type (Basic/WAV) to a proper service level.
    // Defaulting to door to door option for both ambulatory and non-ambulatory.
    switch (vehicleType.code) {
      case VehicleTypeConstants.VEHICLE_TYPE_CODE_BASIC:
        return (
          organizationServiceLevels.find(osl => osl.serviceLevel.code === ServiceLevelConstants.AMBULATORY_DOOR_TO_DOOR_CODE)
            ?.rideNowEnabled ?? false
        );
      case VehicleTypeConstants.VEHICLE_TYPE_CODE_WAV:
        return (
          organizationServiceLevels.find(osl => osl.serviceLevel.code === ServiceLevelConstants.NON_AMBULATORY_DOOR_TO_DOOR_CODE)
            ?.rideNowEnabled ?? false
        );
      default:
        return false;
    }
  }

  static isScheduledEnabled(vehicleType: VehicleType, organizationServiceLevels: Array<OrganizationServiceLevel>): boolean {
    // Convert the vehicle type (Basic/WAV) to a proper service level.
    // Defaulting to door to door option for both ambulatory and non-ambulatory.
    switch (vehicleType.code) {
      case VehicleTypeConstants.VEHICLE_TYPE_CODE_BASIC:
        return (
          organizationServiceLevels.find(osl => osl.serviceLevel.code === ServiceLevelConstants.AMBULATORY_DOOR_TO_DOOR_CODE)
            ?.scheduledEnabled ?? false
        );
      case VehicleTypeConstants.VEHICLE_TYPE_CODE_WAV:
        return (
          organizationServiceLevels.find(osl => osl.serviceLevel.code === ServiceLevelConstants.NON_AMBULATORY_DOOR_TO_DOOR_CODE)
            ?.scheduledEnabled ?? false
        );
      default:
        return false;
    }
  }

  static isWillCallEnabled(vehicleType: VehicleType, organizationServiceLevels: Array<OrganizationServiceLevel>): boolean {
    // Convert the vehicle type (Basic/WAV) to a proper service level.
    // Defaulting to door to door option for both ambulatory and non-ambulatory.
    switch (vehicleType.code) {
      case VehicleTypeConstants.VEHICLE_TYPE_CODE_BASIC:
        return (
          organizationServiceLevels.find(osl => osl.serviceLevel.code === ServiceLevelConstants.AMBULATORY_DOOR_TO_DOOR_CODE)
            ?.willCallEnabled ?? false
        );
      case VehicleTypeConstants.VEHICLE_TYPE_CODE_WAV:
        return (
          organizationServiceLevels.find(osl => osl.serviceLevel.code === ServiceLevelConstants.NON_AMBULATORY_DOOR_TO_DOOR_CODE)
            ?.willCallEnabled ?? false
        );
      default:
        return false;
    }
  }

  static isAllowedRideRequestType(
    rideRequestTypeCode: string,
    isRideNowEnabled: boolean,
    isScheduledEnabled: boolean,
    isWillCallEnabled: boolean
  ) {
    if (rideRequestTypeCode === RideRequestTypeConstants.RECOVERY) {
      return true;
    }
    if (rideRequestTypeCode === RideRequestTypeConstants.RIDE_NOW) {
      return isRideNowEnabled;
    }
    if (rideRequestTypeCode === RideRequestTypeConstants.SCHEDULED) {
      return isScheduledEnabled;
    }
    if (rideRequestTypeCode === RideRequestTypeConstants.WILL_CALL) {
      return isWillCallEnabled;
    }
    return false;
  }
}
